import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError } from './lib/api.js';
import { RouterProvider } from './lib/router.js';
import { SessionProvider } from './lib/session.js';
import { ToastProvider } from './components/toast.js';
import { ViewportDiag } from './components/ViewportDiag.js';
import { ErrorLogDiag } from './components/ErrorLogDiag.js';
import { isDiagOn } from './lib/diag.js';
import { installViewport } from './lib/viewport.js';
import { recoverFromStaleChunks, watchForUpdates } from './lib/app-update.js';
import { App } from './App.js';
import './styles/theme.css';

/*
 * Vigilancia del viewport de la aplicacion instalada.
 *
 * NO decide el alto —eso lo resuelve `theme.css` con `100vh` en modo instalado
 * y `100dvh` en el navegador, que es lo unico correcto en cada uno—. Lo que
 * hace es mantener la marca `data-standalone`, de la que cuelga el ancla del
 * cuerpo, y CURAR el viewport de WebKit cuando se queda encogido tras el
 * teclado: en la PWA de iOS se encoge al abrirlo y no vuelve a crecer en toda
 * la sesion, que en una caja registradora es todos los dias a la primera venta.
 *
 * Va aqui arriba, fuera de todo componente, para que la marca este puesta
 * cuanto antes; dentro de un `useEffect` correria despues del primer pintado.
 *
 * No se guarda la funcion que devuelve: vive lo que viva la pestaña.
 */
installViewport();


/**
 * Cache del servidor.
 *
 * Las invalidaciones llegan por WebSocket, asi que no hace falta consultar en
 * bucle. Se conserva `refetchOnWindowFocus` porque cuando el celular estuvo
 * bloqueado un rato el socket pudo morir sin avisar, y volver a la pantalla es
 * justo el momento en que el dueño quiere ver datos frescos.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // No tiene caso reintentar un permiso denegado o una sesion vencida:
        // el resultado sera el mismo y solo retrasa el mensaje al usuario.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      // Cobrar dos veces por un reintento automatico seria mucho peor que
      // mostrar el error y dejar que la persona decida.
      retry: false,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('no se encontro el elemento #root');

/**
 * Bandera de diagnostico. Enciende DOS sondas, y no tienen la misma vida:
 *
 *   - `ViewportDiag`  TEMPORAL. Existe para cerrar el caso del marco en iOS y se
 *                     borra cuando quede cerrado. Ocupa la franja de ARRIBA.
 *   - `ErrorLogDiag`  PERMANENTE. Es la unica ventana a lo que falla en un
 *                     aparato que no se puede conectar a un depurador; sin ella,
 *                     la bitacora de `lib/error-log.ts` no se puede leer desde la
 *                     tablet del mostrador. Va ABAJO A LA DERECHA para no
 *                     encimarse con la otra.
 *
 * Van AQUI y no dentro de `App` porque tienen que verse en cualquier estado —
 * pantalla de acceso incluida—, y `App` retorna antes de llegar al marco cuando
 * no hay sesion.
 *
 * COMO SE ENCIENDEN: con `?diag=1` desde el navegador, o con CINCO TOQUES en el
 * logotipo desde cualquier parte. El gesto no es un lujo — es la UNICA via en la
 * aplicacion instalada, y ahi vive el defecto que la sonda del marco existe para
 * medir.
 *
 * Aqui decia que bastaba encenderlo en el navegador "porque queda en
 * localStorage". ES FALSO EN iOS: una app agregada a la pantalla de inicio tiene
 * su propio almacenamiento, separado del de Safari. O sea que la sonda se
 * construyo para medir `standalone` y nunca se ha podido encender ahi; lo que se
 * media era el navegador, que es justo donde el defecto no aparece. Ver
 * `lib/diag.ts`.
 */
const DIAG = isDiagOn();

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider>
        <SessionProvider>
          <ToastProvider>
            <App />
            {DIAG ? <ViewportDiag /> : null}
            {DIAG ? <ErrorLogDiag /> : null}
          </ToastProvider>
        </SessionProvider>
      </RouterProvider>
    </QueryClientProvider>
  </StrictMode>,
);

// El service worker solo se registra en produccion: en desarrollo interceptaria
// los modulos de Vite y dejaria de verse el codigo recien guardado.
//
// Con el registro en la mano empieza la vigilancia de versiones nuevas, que es
// la que enciende el boton de "Actualizar" del marco (`lib/app-update.ts`). La
// recuperacion de pantallas que ya no pueden abrir no necesita service worker,
// y por eso va aparte.
if (import.meta.env.PROD) {
  recoverFromStaleChunks();
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      void navigator.serviceWorker.register('/sw.js').then(watchForUpdates, () => {
        // Sin service worker la aplicacion funciona igual; solo pierde el
        // arranque instantaneo, la pantalla de "sin conexion" y el aviso de
        // version nueva (abrirla otra vez sigue trayendo la ultima).
      });
    });
  }
}
